import { FC } from 'react';
import { SERVICE_PROVIDERS } from 'modules/graphql';
import { useGraphql } from 'utils/hooks/useGraphql';
import { useWhatsAppMessageChannelFeature } from 'utils/hooks/featureFlags';
import { fetchEspAccountsQuery, fetchEspAccountsQueryWithWhatsApp } from 'api/queries/espAccounts';
import { fetchEspDefinitionsQuery, FetchEspDefinitionsResponse } from 'api/queries/espDefinitions';
import { fetchVerificationProfilesQuery } from 'api/queries/verificationProfiles';
import { useEspAccountsJson } from 'api/ESPAccountsAPI';
import { useChannelsQuery } from 'api/ChannelsAPI';
import { useMessageTypesQuery } from 'api/MessageTypeAPI';
import { useSmsSendersQuery } from 'api/SendersAPI';
import { FetchEspAccountsResponse, FetchVerificationProfilesResponse } from '../types';
import { SendingPlatformsQueryContext } from './SendingPlatformsQueryContext';
import { mergeEspAccounts } from '../utils';

export const SendingPlatformsQueryContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { isWhatsAppEnabled: isWhatsAppFFOn } = useWhatsAppMessageChannelFeature();
  const {
    data: { espAccounts: espAccountsGraphql = [] } = {},
    error: espAccountsError,
    refetch: refetchEspAccountsGraphql,
    isLoading: isLoadingEspAccounts,
  } = useGraphql<FetchEspAccountsResponse>(
    isWhatsAppFFOn ? fetchEspAccountsQueryWithWhatsApp : fetchEspAccountsQuery,
    { errorPolicy: 'all' },
  );
  const {
    data: { verificationProfiles = [] } = {},
    error: verificationError,
    refetch: refetchVerificationProfiles,
    isLoading: isLoadingVerificationProfiles,
  } = useGraphql<FetchVerificationProfilesResponse>(fetchVerificationProfilesQuery);

  const {
    data: { espDefinitions = [] } = {},
    error: espDefinitionsError,
    isLoading: isLoadingEspDefinitions,
  } = useGraphql<FetchEspDefinitionsResponse>(fetchEspDefinitionsQuery);
  const {
    data: espAccountsRest = [],
    error: espAccountsJsonError,
    refetch: refetchEspAccountsRest,
  } = useEspAccountsJson();
  const {
    data: emailChannels = [],
    error: channelsError,
    refetch: refetchEmailChannels,
  } = useChannelsQuery();
  const {
    data: messageTypes = [],
    error: messageTypesError,
    refetch: refetchMessageTypes,
  } = useMessageTypesQuery();
  const {
    data: { smsSenders = [] } = {},
    error: smsSenderError,
    refetch: refetchSmsSenders,
  } = useSmsSendersQuery();

  const refetchEspAccounts = () => {
    refetchEspAccountsGraphql();
    refetchEspAccountsRest();
  };

  const espAccounts = mergeEspAccounts(espAccountsGraphql, espAccountsRest);

  const hasError = Boolean(
    channelsError ||
      espAccountsError ||
      espDefinitionsError ||
      espAccountsJsonError ||
      messageTypesError ||
      smsSenderError ||
      verificationError,
  );

  return (
    <SendingPlatformsQueryContext.Provider
      value={{
        espAccounts,
        espDefinitions: [
          ...(espDefinitions as SERVICE_PROVIDERS[]),
          ...(isWhatsAppFFOn ? (['InfoBip'] as SERVICE_PROVIDERS[]) : []),
        ],
        hasError,
        emailChannels,
        messageTypes,
        smsSenders,
        verificationProfiles,
        refetchEspAccounts,
        refetchEmailChannels,
        refetchMessageTypes,
        refetchSmsSenders,
        refetchVerificationProfiles,
        isLoadingEspAccounts,
        isLoadingEspDefinitions,
        isLoadingVerificationProfiles,
      }}
    >
      {children}
    </SendingPlatformsQueryContext.Provider>
  );
};
